import {
  IJournal,
  Measures,
  IProduct,
  IBox,
  IEntityType,
} from "../types/entities";
import { Type } from "./Type";
import { Product } from "./Product";
import { Journal } from "./Journal";
import { Box } from "./Box";
import {
  action,
  autorun,
  computed,
  makeObservable,
  observable,
  runInAction,
} from "mobx";
import type { InjectionToken } from "tsyringe";
import { inject, injectable } from "tsyringe";
import type { IWarehouseApi } from "../api/WarehouseApi";
import { WAREHOUSE_API_TOKEN } from "../api/WarehouseApi";
import { setBoxResolver } from "./relations";
import { formatTypeTag, resolveEntityTypeTags } from "../utils/typeTags";

interface StoreSnapshot {
  journals: IJournal[];
  products: IProduct[];
  boxes: IBox[];
  boxTypes: IEntityType[];
  productTypes: IEntityType[];
  searchText: string;
  searchMeasureFilter: SearchMeasureFilter;
  searchPlacementFilter: SearchPlacementFilter;
  searchTypeFilter: string;
}

export type SearchMeasureFilter = "all" | Measures;
export type SearchPlacementFilter = "all" | "inBox" | "withoutBox";

export interface SearchTypeFilterOption {
  id: string;
  name: string;
}

export type SearchResultItem =
  | { kind: "product"; item: Product }
  | { kind: "box"; item: Box };

export interface IStore {
  journals: Journal[];
  products: Product[];
  boxes: Box[];
  boxTypes: Type[];
  productTypes: Type[];
  searchText: string;
  searchError: string | null;
  isSearchLoading: boolean;
  searchMeasureFilter: SearchMeasureFilter;
  searchPlacementFilter: SearchPlacementFilter;
  searchTypeFilter: string;
  searchTypeOptions: SearchTypeFilterOption[];
  hasActiveSearchFilters: boolean;
  searchItems: SearchResultItem[];
  getFromIdResult: Product | Box | null;
  QRResult: (Product | Box)[];
  selectedBox: Box | null;
  selectedProduct: Box | null;
  search: (text: string) => void;
  addProduct: (product: Product) => void;
  addBox: (box: Box) => void;
  addJournal: (journal: Journal) => void;
  getFromId: (id: string) => void;
  openBox: (box: Box) => void;
  openProduct: (product: Product) => void;
  setSearchMeasureFilter: (filter: SearchMeasureFilter) => void;
  setSearchPlacementFilter: (filter: SearchPlacementFilter) => void;
  setSearchTypeFilter: (filter: string) => void;
  clearSearchFilters: () => void;
  asJSON: StoreSnapshot;
}

export const STORE_TOKEN = Symbol("IStore") as InjectionToken<IStore>;
type ModelConstructor<Input, Model> = new (input: Input) => Model;

const maker = <Input, Model>(
  items: Input[],
  Constructor: ModelConstructor<Input, Model>,
): Model[] => {
  return items ? items.map((item) => new Constructor(item)) : [];
};

function normalizeSearchText(value: string): string {
  return value.trim().replace(/^#+/g, "").toLocaleLowerCase("ru-RU");
}

function matchesProductByQuery(
  product: Product,
  normalizedQuery: string,
  typeTags: readonly string[],
): boolean {
  if (!normalizedQuery) {
    return true;
  }

  const fields = [
    product.id,
    product.name,
    product.description,
    ...typeTags,
    product.parentId || "",
  ];
  return fields.some((field) =>
    field.toLocaleLowerCase("ru-RU").includes(normalizedQuery),
  );
}

function matchesBoxByQuery(
  box: Box,
  normalizedQuery: string,
  typeTags: readonly string[],
): boolean {
  if (!normalizedQuery) {
    return true;
  }

  const fields = [
    box.id,
    box.name,
    box.description,
    ...typeTags,
    box.parentId || "",
  ];
  return fields.some((field) =>
    field.toLocaleLowerCase("ru-RU").includes(normalizedQuery),
  );
}

function dedupeProducts(items: Product[]): Product[] {
  const map = new Map<string, Product>();
  items.forEach((item) => {
    map.set(item.id, item);
  });
  return Array.from(map.values());
}

function dedupeBoxes(items: Box[]): Box[] {
  const map = new Map<string, Box>();
  items.forEach((item) => {
    map.set(item.id, item);
  });
  return Array.from(map.values());
}

const mock: StoreSnapshot = {
  journals: [],
  products: [
    {
      id: "prod1",
      name: "Дрель",
      photo: "http://placehold.it/50x50",
      description: "хорошая дрель",
      manufacturingDate: { day: 1, minute: 1, month: 1, hour: 1, year: 2020 },
      expiryDate: null,
      typeTags: ["электроинструмент", "мастерская"],
      measure: Measures.quantity,
      quantity: 1,
      parentId: "box1",
    },
    {
      id: "prod2",
      name: "Саморезы",
      photo: "",
      description: "по дереву",
      manufacturingDate: { day: 1, minute: 1, month: 1, hour: 1, year: 2020 },
      expiryDate: null,
      typeTags: ["крепеж"],
      measure: Measures.quantity,
      quantity: 1,
      parentId: "box2",
    },
  ],
  boxes: [
    {
      id: "box1",
      name: "Коробка с инструментами",
      photo: "",
      typeTags: ["инструменты"],
      description: "пластиковая коробка",
      parentId: null,
    },
    {
      id: "box2",
      name: "Коробка с болтами",
      photo: "",
      typeTags: ["крепеж"],
      description: "пластиковая коробка",
      parentId: null,
    },
  ],
  productTypes: [
    {
      id: "type1",
      name: "электроинструмент",
    },
    {
      id: "type2",
      name: "саморезы",
    },
  ],
  boxTypes: [
    {
      id: "boxType1",
      name: "Коробка",
    },
  ],
  searchText: "",
  searchMeasureFilter: "all",
  searchPlacementFilter: "all",
  searchTypeFilter: "all",
};

@injectable()
export class Store implements IStore {
  private searchRequestId = 0;

  constructor(
    @inject(WAREHOUSE_API_TOKEN) private readonly api: IWarehouseApi,
  ) {
    makeObservable(this);

    const fromStorage = JSON.parse(localStorage.getItem("store") || "null");
    const {
      journals,
      products,
      boxes,
      boxTypes,
      productTypes,
      searchText,
      searchMeasureFilter,
      searchPlacementFilter,
      searchTypeFilter,
    }: StoreSnapshot = fromStorage ? fromStorage : mock;
    this.journals = maker<IJournal, Journal>(journals, Journal);
    this.products = maker<IProduct, Product>(products, Product);
    this.boxes = maker<IBox, Box>(boxes, Box);
    this.boxTypes = maker<IEntityType, Type>(boxTypes, Type);
    this.productTypes = maker<IEntityType, Type>(productTypes, Type);
    this.searchText = searchText || "";
    this.searchMeasureFilter = searchMeasureFilter || "all";
    this.searchPlacementFilter = searchPlacementFilter || "all";
    this.searchTypeFilter = searchTypeFilter || "all";
    setBoxResolver((id) => this.boxes.find((box) => box.id === id));

    autorun(() => {
      localStorage.setItem("store", JSON.stringify(this.asJSON));
    });
  }

  @observable
  journals: Journal[];

  @observable
  products: Product[];

  @observable
  boxes: Box[];

  @observable
  boxTypes: Type[];

  @observable
  productTypes: Type[];

  @observable
  searchText: string;

  @observable
  searchError: string | null = null;

  @observable
  isSearchLoading: boolean = false;

  @observable
  searchMeasureFilter: SearchMeasureFilter = "all";

  @observable
  searchPlacementFilter: SearchPlacementFilter = "all";

  @observable
  searchTypeFilter: string = "all";

  @observable
  private searchProductResults: Product[] = [];

  @observable
  private searchBoxResults: Box[] = [];

  @computed
  get searchItems(): SearchResultItem[] {
    const products = this.searchProductResults.filter((product) => {
      if (
        this.searchMeasureFilter !== "all" &&
        product.measure !== this.searchMeasureFilter
      ) {
        return false;
      }

      if (this.searchPlacementFilter === "inBox" && !product.parentId) {
        return false;
      }

      if (this.searchPlacementFilter === "withoutBox" && product.parentId) {
        return false;
      }

      if (
        this.searchTypeFilter !== "all" &&
        !this.getProductTypeTags(product).includes(this.searchTypeFilter)
      ) {
        return false;
      }

      return true;
    });

    const canShowBoxes = this.searchMeasureFilter === "all";
    const boxes = canShowBoxes
      ? this.searchBoxResults.filter((box) => {
          if (this.searchPlacementFilter === "inBox" && !box.parentId) {
            return false;
          }

          if (this.searchPlacementFilter === "withoutBox" && box.parentId) {
            return false;
          }

          if (
            this.searchTypeFilter !== "all" &&
            !this.getBoxTypeTags(box).includes(this.searchTypeFilter)
          ) {
            return false;
          }

          return true;
        })
      : [];

    return [
      ...boxes.map((item) => ({ kind: "box" as const, item })),
      ...products.map((item) => ({ kind: "product" as const, item })),
    ];
  }

  @computed
  get searchTypeOptions(): SearchTypeFilterOption[] {
    const optionsMap = new Map<string, string>();

    const appendTags = (tags: readonly string[]): void => {
      tags.forEach((tag) => {
        if (!tag || optionsMap.has(tag)) {
          return;
        }

        optionsMap.set(tag, formatTypeTag(tag));
      });
    };

    this.productTypes.forEach((type) => {
      appendTags(
        resolveEntityTypeTags({
          typeTags: [],
          fallbackTypeName: type.name,
        }),
      );
    });

    this.boxTypes.forEach((type) => {
      appendTags(
        resolveEntityTypeTags({
          typeTags: [],
          fallbackTypeName: type.name,
        }),
      );
    });

    this.searchProductResults.forEach((product) => {
      appendTags(this.getProductTypeTags(product));
    });

    this.searchBoxResults.forEach((box) => {
      appendTags(this.getBoxTypeTags(box));
    });

    return Array.from(optionsMap.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((left, right) => left.name.localeCompare(right.name, "ru"));
  }

  @computed
  get hasActiveSearchFilters(): boolean {
    return (
      this.searchMeasureFilter !== "all" ||
      this.searchPlacementFilter !== "all" ||
      this.searchTypeFilter !== "all"
    );
  }

  @action
  search(text: string) {
    this.searchText = text;
    this.searchError = null;

    const query = text.trim();
    const requestId = ++this.searchRequestId;
    if (query.length < 2) {
      this.searchProductResults = [];
      this.searchBoxResults = [];
      this.isSearchLoading = false;
      return;
    }

    this.isSearchLoading = true;
    void this.performServerSearch(query, requestId);
  }

  @action
  addProduct(product: Product) {
    this.products.push(product);
  }

  @action
  addBox(box: Box) {
    this.boxes.push(box);
  }

  @action
  addJournal(journal: Journal) {
    this.journals.push(journal);
  }

  @action
  setSearchMeasureFilter(filter: SearchMeasureFilter) {
    this.searchMeasureFilter = filter;
  }

  @action
  setSearchPlacementFilter(filter: SearchPlacementFilter) {
    this.searchPlacementFilter = filter;
  }

  @action
  setSearchTypeFilter(filter: string) {
    this.searchTypeFilter = filter;
  }

  @action
  clearSearchFilters() {
    this.searchMeasureFilter = "all";
    this.searchPlacementFilter = "all";
    this.searchTypeFilter = "all";
  }

  @action
  getFromId(id: string) {
    const box = this.boxes.find((box) => box.id === id);

    if (box) {
      this.getFromIdResult = box;
      return;
    } else {
      const product = this.products.find((product) => product.id === id);
      if (product) {
        this.getFromIdResult = product;
        return;
      }
    }
    this.getFromIdResult = null;
    return;
  }

  @observable
  getFromIdResult: Product | Box | null = null;

  @computed
  get QRResult(): (Product | Box)[] {
    if (this.getFromIdResult) {
      const items: (Product | Box)[] = [this.getFromIdResult];

      const getItems = (elem: Product | Box): void => {
        const parent = elem.parent as Box;
        if (parent) {
          items.push(parent);
          getItems(parent);
        }
      };

      getItems(this.getFromIdResult);

      return items.reverse();
    }
    return [];
  }

  @observable
  selectedBox: Box | null = null;

  @action
  openBox = (box: Box) => {
    this.selectedBox = box;
  };

  @observable
  selectedProduct: Box | null = null;

  @action
  openProduct = (product: Product) => {
    this.selectedProduct = product;
  };

  @computed
  get asJSON(): StoreSnapshot {
    return {
      journals: this.journals,
      products: this.products,
      boxes: this.boxes,
      boxTypes: this.boxTypes,
      productTypes: this.productTypes,
      searchText: this.searchText,
      searchMeasureFilter: this.searchMeasureFilter,
      searchPlacementFilter: this.searchPlacementFilter,
      searchTypeFilter: this.searchTypeFilter,
    };
  }

  private async performServerSearch(
    query: string,
    requestId: number,
  ): Promise<void> {
    try {
      const [items, boxes] = await Promise.all([
        this.api.searchProducts(query),
        this.api.searchBoxes(query),
      ]);
      if (requestId !== this.searchRequestId) {
        return;
      }

      const normalizedQuery = normalizeSearchText(query);
      let productResults = items
        .map((item) => new Product(item))
        .filter((item) =>
          matchesProductByQuery(
            item,
            normalizedQuery,
            this.getProductTypeTags(item),
          ),
        );
      let boxResults = boxes
        .map((item) => new Box(item))
        .filter((item) =>
          matchesBoxByQuery(item, normalizedQuery, this.getBoxTypeTags(item)),
        );

      if (productResults.length === 0 && boxResults.length === 0) {
        // Lightweight fallback: only local cache, no heavy full-table API requests.
        productResults = this.products.filter((item) =>
          matchesProductByQuery(
            item,
            normalizedQuery,
            this.getProductTypeTags(item),
          ),
        );
        boxResults = this.boxes.filter((item) =>
          matchesBoxByQuery(item, normalizedQuery, this.getBoxTypeTags(item)),
        );
      }

      runInAction(() => {
        this.searchProductResults = dedupeProducts(productResults);
        this.searchBoxResults = dedupeBoxes(boxResults);
        this.isSearchLoading = false;
      });
    } catch (error) {
      if (requestId !== this.searchRequestId) {
        return;
      }

      const normalizedQuery = normalizeSearchText(query);
      runInAction(() => {
        this.searchProductResults = dedupeProducts(
          this.products.filter((product) =>
            matchesProductByQuery(
              product,
              normalizedQuery,
              this.getProductTypeTags(product),
            ),
          ),
        );
        this.searchBoxResults = dedupeBoxes(
          this.boxes.filter((box) =>
            matchesBoxByQuery(box, normalizedQuery, this.getBoxTypeTags(box)),
          ),
        );
        this.searchError =
          error instanceof Error
            ? `${error.message}. Показаны локальные результаты.`
            : "Не удалось выполнить поиск по серверу";
        this.isSearchLoading = false;
      });
    }
  }

  private getProductTypeTags(product: Product): string[] {
    return resolveEntityTypeTags({
      typeTags: product.typeTags,
    });
  }

  private getBoxTypeTags(box: Box): string[] {
    return resolveEntityTypeTags({
      typeTags: box.typeTags,
    });
  }
}
